import { z } from "zod";

/**
 * What a stored credential is for. The purpose decides which steps may bind it, which is how a
 * run is kept away from a write credential.
 */
export const credentialPurposes = [
  "read-repository",
  "write-repository",
  "runtime-service",
  "model-key",
] as const;
export const credentialPurposeSchema = z.enum(credentialPurposes);
export type CredentialPurpose = z.infer<typeof credentialPurposeSchema>;

/** Register a local credential reference. The value is accepted once and never echoed. */
export const createCredentialSchema = z
  .object({
    ref: z.string().trim().min(1).max(200),
    purpose: credentialPurposeSchema,
    value: z.string().min(1).max(65_536),
    expiresAt: z.string().datetime().nullable().optional(),
  })
  .strict();
export type CreateCredential = z.infer<typeof createCredentialSchema>;

/**
 * One credential, as `GET /api/v1/projects/:id/credentials` returns it. There is no value field,
 * and no endpoint returns one: the page shows that a credential exists and whether it is valid,
 * never what it is. Rotating replaces the value server-side and expires the old one.
 */
export const credentialSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  ref: z.string().min(1),
  purpose: credentialPurposeSchema,
  valid: z.boolean(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  rotatedAt: z.string().datetime().nullable(),
});
export type Credential = z.infer<typeof credentialSchema>;

export const credentialListSchema = z.array(credentialSchema);
export type CredentialList = z.infer<typeof credentialListSchema>;

/**
 * `POST /api/v1/projects/:id/credentials/:ref/rotate`. The body carries the new value once.
 * The response does not carry it back, so a rotated value cannot leak through the API.
 */
export const rotateCredentialSchema = z.object({
  value: z.string().min(1).max(65_536),
  expiresAt: z.string().datetime().nullable().optional(),
});
export type RotateCredential = z.infer<typeof rotateCredentialSchema>;

/** Device-code flow states, as the device list badges them. */
export const deviceCodeStates = ["pending", "approved", "denied", "expired"] as const;
export const deviceCodeStateSchema = z.enum(deviceCodeStates);
export type DeviceCodeState = z.infer<typeof deviceCodeStateSchema>;

/**
 * `POST /api/v1/projects/:id/devices/code` starts the flow. The code is shown once to the
 * person who is approving. The initiating client receives its token once through an HttpOnly,
 * Secure, SameSite=Strict cookie, never a JSON body. Only its hash is stored, and it remains
 * inactive until owner approval. Device session reads return connection metadata only.
 */
export const deviceCodeSchema = z.object({
  code: z.string().min(1),
  client: z.string().min(1),
  state: deviceCodeStateSchema,
  expiresAt: z.string().datetime(),
});
export type DeviceCode = z.infer<typeof deviceCodeSchema>;

export const startDeviceCodeSchema = z.object({
  client: z.string().trim().min(1).max(200),
});
export type StartDeviceCode = z.infer<typeof startDeviceCodeSchema>;

/** `POST /api/v1/projects/:id/devices/approve` approves a pending code. */
export const approveDeviceSchema = z.object({
  code: z.string().trim().min(1).max(200),
  projectId: z.uuid(),
});
export type ApproveDevice = z.infer<typeof approveDeviceSchema>;

/**
 * One connected client, as `GET /api/v1/projects/:id/devices` returns it. There is no token
 * field. Revoking a device blocks its next call.
 */
export const deviceConnectionSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  client: z.string().min(1),
  lastUsedAt: z.string().datetime().nullable(),
  connectedAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
});
export type DeviceConnection = z.infer<typeof deviceConnectionSchema>;

export const deviceConnectionListSchema = z.array(deviceConnectionSchema);
export type DeviceConnectionList = z.infer<typeof deviceConnectionListSchema>;
